import React from 'react';
import {Line} from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import moment from 'moment';
import {DATE_FORMAT, DATETIME_SHORT_FORMAT} from 'constants/uiConstants';
import {convertSecondsToTimeString} from 'utils/Utils';

ChartJS.register(CategoryScale, Legend, LineElement, LinearScale, PointElement, TimeScale, Tooltip, zoomPlugin);

const month = moment.duration('PT720H');  // 720 hours (30 days)

const colorSets = [
    {backgroundColor: 'rgba(0, 128, 128, 0.2)',   borderColor: 'rgba(0, 128, 128, 1.0)'},    // teal
    {backgroundColor: 'rgba(165, 42, 42, 0.2)',   borderColor: 'rgba(165, 42, 42, 1.0)'},    // brown
    {backgroundColor: 'rgba(50, 205, 50, 0.2)',   borderColor: 'rgba(50, 205, 50, 1.0)'},    // lime
    {backgroundColor: 'rgba(75, 0, 130, 0.2)',    borderColor: 'rgba(75, 0, 130, 1.0)'},     // indigo
    {backgroundColor: 'rgba(255, 159, 64, 0.2)',  borderColor: 'rgba(255, 159, 64, 1.0)'},   // orange
    {backgroundColor: 'rgba(54, 162, 235, 0.2)',  borderColor: 'rgba(54, 162, 235, 1.0)'},   // blue
    {backgroundColor: 'rgba(255, 99, 132, 0.2)',  borderColor: 'rgba(255, 99, 132, 1.0)'},   // red
    {backgroundColor: 'rgba(153, 102, 255, 0.2)', borderColor: 'rgba(153, 102, 255, 1.0)'},  // purple
    {backgroundColor: 'rgba(255, 205, 86, 0.2)',  borderColor: 'rgba(255, 205, 86, 1.0)'},   // yellow
    {backgroundColor: 'rgba(75, 192, 192, 0.2)',  borderColor: 'rgba(75, 192, 192, 1.0)'},   // green
    {backgroundColor: 'rgba(255, 99, 255, 0.2)',  borderColor: 'rgba(255, 99, 255, 1.0)'},   // pink
    {backgroundColor: 'rgba(201, 203, 207, 0.2)', borderColor: 'rgba(201, 203, 207, 1.0)'},  // grey
];

const lineConfig = {
    current_avg: {},
    current_max: {},
    loadcell_avg: {},
    loadcell_max: {},
    motor_in_rpm_avg: {},
    motor_in_rpm_max: {},
    number_of_start_stop_cumulative: {
        beginAtZero: false,
        tension: 0,
    },
    number_of_start_stop_hourly: {
        beginAtZero: true,
        tension: 0,
    },
    run_time_cumulative: {
        beginAtZero: false,
        tension: 0,
    },
    run_time_hourly: {
        beginAtZero: true,
        tension: 0,
    },
};

const formatYAxis = (parameter, value) => {
    if (parameter.startsWith('run_time')) return convertSecondsToTimeString(value);
    return value;
};

const formatTooltip = tooltipItemContext => {
    let value = tooltipItemContext.parsed.y;
    if (tooltipItemContext.dataset.label.startsWith('Run Time')) value = convertSecondsToTimeString(value);
    return `${tooltipItemContext.dataset.label}: ${value}`;
};

export default function LineChart({chartDates, data, parametersMap, parameters, enableZoom = false}) {

    const timestamps = data.map(d => d.ts);
    const duration = moment.duration(chartDates[1].diff(chartDates[0]));
    const isDurationOverMonth = duration >= month;
    const tickFormat = isDurationOverMonth ? DATE_FORMAT : DATETIME_SHORT_FORMAT;

    // the chart options
    const options = {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        normalized: true,
        plugins: {
            legend: {position: 'top'},
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    // title: items => moment.unix(items[0].parsed.x / 1000).format(DATETIME_SHORT_FORMAT),
                    label: formatTooltip,
                }
            },
        },
        scales: {
            x: {
                type: 'time',
                time: {tooltipFormat: 'MM/dd/yyyy HH:mm'},
                title: {display: false, text: 'Date'},
                ticks: {
                    callback: unixTime => timestamps.includes(unixTime) ? moment.unix(unixTime / 1000).format(tickFormat) : null,
                },
                grid: {
                    drawTicks: true,
                },
            },
        }
    };
    if (enableZoom) options.plugins.zoom = {
        limits: {
            x: {min: 'original', max: 'original'},
            // axis limits are set below
        },
        pan: {
            enabled: true,
            mode: 'x',
            scaleMode: 'x',
        },
        zoom: {
            wheel: {
                enabled: true,
                speed: 0.3,
            },
            pinch: {
                enabled: true,
            },
            mode: 'x',
        }
    };

    parameters.forEach((parameter, idx) => {
        const parameterConfig = lineConfig[parameter];
        const beginAtZero = parameterConfig?.beginAtZero !== undefined ? parameterConfig.beginAtZero : true;
        options.scales[parameter] = {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: beginAtZero,
            title: {display: true, text: parametersMap[parameter], color: colorSets[idx].borderColor},
            ticks: {callback: value => formatYAxis(parameter, value)},
        };
        // options.plugins.zoom.limits[parameter] = {min: 'original', max: 'original'};
    });

    // preparing the chart data
    const chartData = {
        datasets: [],
    };
    // https://www.chartjs.org/docs/latest/charts/line.html
    parameters.forEach((parameter, idx) => {
        const parameterData = data.map(pt => ({x: pt.ts, y: pt[parameter]}));
        const parameterConfig = lineConfig[parameter];
        const tension = parameterConfig?.tension !== undefined ? parameterConfig.tension : 0.1;
        chartData.datasets.push({
            label: parametersMap[parameter],
            data: parameterData,
            borderWidth: 2,
            borderColor: colorSets[idx].borderColor,
            backgroundColor: colorSets[idx].backgroundColor,
            cubicInterpolationMode: 'default',
            yAxisID: parameter,
            tension: tension,
            pointRadius: 2,
            // spanGaps: 5000,  // 5 seconds
        })
    });

    return <Line options={options} data={chartData} />;
};
